import get from 'lodash/get';
import PropTypes from 'prop-types';
import {Component} from 'react';
import {Form, Grid, Header, Segment} from 'semantic-ui-react';
import CoverPagePreviewContainer from './CoverPagePreviewContainer';
import './CustomiseCoverPage.scss';
import CustomiseCoverPageDetailsModalContainer from './CustomiseCoverPageDetailsModalContainer';
import CustomiseCoverPageImagesModalContainer from './CustomiseCoverPageImagesModalContainer';
import {OptionalAddressTitle} from './OptionalAddressTitle';
import OptionalTitle from './OptionalTitle';
import SelectCoverPageImage from './SelectCoverPageImage';

export default class CustomiseCoverPage extends Component {
  static propTypes = {
    propertyId: PropTypes.number.isRequired,
    reportType: PropTypes.string.isRequired,
    section: PropTypes.object.isRequired,
    selectedImage: PropTypes.shape({
      half: PropTypes.string,
      full: PropTypes.string,
    }).isRequired,
    selectedLayout: PropTypes.string.isRequired,
    otherImages: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    franchiseThumbnails: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    colour: PropTypes.string,
    onSelectImage: PropTypes.func.isRequired,
    onSelectLayout: PropTypes.func.isRequired,
    updateAddressPreference: PropTypes.func.isRequired,
    updateSectionPreference: PropTypes.func.isRequired,
    featureFlags: PropTypes.shape({}),
  };

  getValue = (label) => {
    const {section} = this.props;
    return get(section, ['sectionData', label]);
  };

  isEnabled = (label) => {
    const {section} = this.props;
    return get(section, `sectionData.${label}_isEnabled`, 'true') === 'true';
  };

  toggleReportData = (label) => {
    return (event, data) => {
      const {updateSectionPreference} = this.props;
      let checked = data.checked.toString();
      updateSectionPreference(checked, `${label}_isEnabled`);
    };
  };

  updateData = (label, funcName) => {
    return (data) => {
      let func = this.props[funcName];
      if ('title' in data) {
        func(data.title, label);
      } else {
        func(data.isEnabled.toString(), `${label}_isEnabled`);
      }
    };
  };

  updateFavouriteData = (label) => {
    return this.updateData(label, 'updateSectionPreference');
  };

  updateCurrentReportData = (label) => {
    return this.updateData(label, 'updateAddressPreference');
  };

  withCustomisations = () => {
    const {
      selectedImage,
      selectedLayout,
      otherImages,
      franchiseThumbnails,
      onSelectImage,
      onSelectLayout,
      propertyId,
      reportType,
      colour,
    } = this.props;

    return (
      <Form className="reportTitle" size="huge">
        <Grid>
          <Grid.Row>
            <Grid.Column width="11">
              <OptionalTitle
                onUpdateData={this.updateFavouriteData('title')}
                isEnabled={this.isEnabled('title')}
                label="Title"
                title={this.getValue('title')}
                maxLength={29}
              />
              <OptionalTitle
                className="preparedFor"
                onUpdateData={this.updateCurrentReportData('preparedFor')}
                isEnabled={this.isEnabled('preparedFor')}
                label="Prepared for"
                placeholder="Enter recipient name"
                title={this.getValue('preparedFor')}
                maxLength={31}
              />
              <OptionalTitle
                className="preparedOn"
                onUpdateData={this.updateCurrentReportData('preparedOn')}
                isEnabled={this.isEnabled('preparedOn')}
                label="Prepared on"
                title={this.getValue('preparedOn')}
                maxLength={24}
              />
              <OptionalTitle
                className="singleLineAddress"
                onUpdateData={this.updateCurrentReportData('singleLineAddress')}
                isEnabled={this.isEnabled('singleLineAddress')}
                label="Property address"
                title={this.getValue('singleLineAddress')}
                maxLength={70}
              />
              <OptionalTitle
                className="agentName"
                onUpdateData={this.updateFavouriteData('agentName')}
                isEnabled={this.isEnabled('agentName')}
                label="Agent Name"
                title={this.getValue('agentName')}
                maxLength={70}
              />
              <OptionalTitle
                className="agency"
                onUpdateData={this.updateFavouriteData('agency')}
                isEnabled={this.isEnabled('agency')}
                label="Agency Name"
                title={this.getValue('agency')}
                maxLength={70}
              />

              <OptionalAddressTitle
                className="agencyAddress"
                updateAddressLine1={this.updateFavouriteData('agencyAddress')}
                updateAddressLine2={this.updateFavouriteData('agencyAddress2')}
                isEnabled={this.isEnabled('agencyAddress')}
                label="Agency Address"
                title1={this.getValue('agencyAddress')}
                title2={this.getValue('agencyAddress2')}
                maxLength={70}
              />

              <OptionalTitle
                className="landline"
                onUpdateData={this.updateFavouriteData('landline')}
                isEnabled={this.isEnabled('landline')}
                label="Landline"
                title={this.getValue('landline')}
                maxLength={70}
              />
              <OptionalTitle
                className="mobile"
                onUpdateData={this.updateFavouriteData('mobile')}
                isEnabled={this.isEnabled('mobile')}
                label="Mobile"
                title={this.getValue('mobile')}
                maxLength={70}
              />
              <OptionalTitle
                className="email"
                onUpdateData={this.updateFavouriteData('email')}
                isEnabled={this.isEnabled('email')}
                label="Email"
                title={this.getValue('email')}
                maxLength={70}
              />
              <Form.Group inline>
                <Form.Checkbox
                  className="agent-photo"
                  checked={this.isEnabled('agentPhoto')}
                  label="Agent Photo"
                  onClick={this.toggleReportData('agentPhoto')}
                />
              </Form.Group>
              <Form.Group inline>
                <Form.Checkbox
                  checked={this.isEnabled('agencyLogo')}
                  label="Agency logo"
                  className="agency-logo"
                  onClick={this.toggleReportData('agencyLogo')}
                />
              </Form.Group>
            </Grid.Column>
            <Grid.Column width="5">
              <SelectCoverPageImage
                otherImages={otherImages}
                franchiseThumbnails={franchiseThumbnails}
                selectedImage={selectedImage}
                selectedLayout={selectedLayout}
                onSelectImage={onSelectImage}
                onSelectLayout={onSelectLayout}
                propertyId={propertyId}
                reportType={reportType}
                colour={colour}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    );
  };

  render() {
    return (
      <div className="coverpage aug2019">
        <Header className="section-type" as="h2" style={{fontWeight: 'bold'}} disabled>Cover Page</Header>
        <div className="cover-controls">
          <CustomiseCoverPageImagesModalContainer/>
          <CustomiseCoverPageDetailsModalContainer/>
        </div>
        <Segment style={{padding: '0px'}}>
          <div className="aug-cover">
            <CoverPagePreviewContainer showPageBorder={false}/>
          </div>
        </Segment>
      </div>
    );
  }
}
